import { withPrometheusMetricsGetServerSideProps } from '@jusbrasil-web/core-next'
import { GetServerSideProps, GetServerSidePropsResult, PreviewData } from 'next'
import { ParsedUrlQuery } from 'querystring'

interface RequestLogFields {
  level: 'info' | 'error'
  statusCode: number
  path: string
  durationMs: number
}

function routeTemplateFromFilename(filename: string): string {
  const segment = filename.includes('pages/') ? filename.split('pages/')[1] : filename
  return `/${segment.replace(/\.(ts|tsx|js|jsx)$/, '')}`
}

function resolveStatusCode<Props>(
  ctx: { res: { statusCode: number } },
  result: GetServerSidePropsResult<Props> | null,
  thrownError: unknown
): number {
  if (thrownError || !result) return 500
  if ('redirect' in result) {
    if ('statusCode' in result.redirect) return result.redirect.statusCode
    return result.redirect.permanent ? 308 : 307
  }
  if ('notFound' in result) return 404
  return ctx.res.statusCode || 200
}

export const withAuthPageLogging = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Props extends { [key: string]: any },
  Params extends ParsedUrlQuery = ParsedUrlQuery,
  Preview extends PreviewData = PreviewData
>(
  filename: string,
  getServerSideProps: GetServerSideProps<Props, Params, Preview>
): GetServerSideProps<Props, Params, Preview> => {
  const wrapped = withPrometheusMetricsGetServerSideProps(filename, getServerSideProps)
  const path = routeTemplateFromFilename(filename)

  return async (ctx) => {
    const start = process.hrtime()
    let result: GetServerSidePropsResult<Props> | null = null
    let thrownError: unknown = null

    try {
      result = await wrapped(ctx)
    } catch (err) {
      thrownError = err
    }

    const end = process.hrtime(start)
    const durationMs = Math.round((end[0] * 1e9 + end[1]) / 1e6)
    const statusCode = resolveStatusCode(ctx, result, thrownError)

    const fields: RequestLogFields = {
      level: statusCode >= 500 ? 'error' : 'info',
      statusCode,
      path,
      durationMs,
    }
    console.log(JSON.stringify(fields))

    if (thrownError) throw thrownError
    return result as GetServerSidePropsResult<Props>
  }
}
